import React from 'react';
import {
  Box,
  Card,
  Chip,
  InputAdornment,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { http, unwrapApiResponse } from '../api/http';
import { PageHeader } from '../components/PageHeader';
import { ErrorBanner } from '../components/ErrorBanner';
import { Legend, Panel, StackedBarChart, StatTile, fmtNumber, useChartColors } from '../components/charts';
import { DateRangeBar } from '../components/DateRangeBar';
import { dailySeries, rangeForDays } from '../utils/dateRange';
import { fmtTokens, fmtUsd } from '../utils/format';
import { useAppUi } from '../app/useAppUi';

const num = (v) => Number(v) || 0;

/** Text field that saves on blur / Enter, only when the value changed. */
function InlineField({ value, onSave, placeholder, adornment, width = 140, type = 'text' }) {
  const [draft, setDraft] = React.useState(value ?? '');
  React.useEffect(() => setDraft(value ?? ''), [value]);
  const commit = () => {
    if (String(draft) !== String(value ?? '')) onSave(draft);
  };
  return (
    <TextField
      size="small"
      type={type}
      value={draft}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.querySelector('input')?.blur()}
      slotProps={{
        htmlInput: type === 'number' ? { min: 0, step: 0.01 } : { maxLength: 60 },
        input: adornment ? { startAdornment: <InputAdornment position="start">{adornment}</InputAdornment> } : undefined,
      }}
      sx={{ width, '& input': { py: 0.6, fontSize: 13 } }}
    />
  );
}

export function AiUsagePage() {
  const { t } = useAppUi();
  const colors = useChartColors();
  const [range, setRange] = React.useState(() => rangeForDays(30));
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [data, setData] = React.useState(null);
  const [reload, setReload] = React.useState(0);

  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    http
      .get('/admin/ai-usage', { params: range })
      .then((res) => alive && setData(unwrapApiResponse(res)))
      .catch((e) => alive && setError(e))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [range, reload]);

  const save = async (fn) => {
    setError(null);
    try {
      await fn();
      setReload((n) => n + 1);
    } catch (e) {
      setError(e);
    }
  };

  const totals = data?.totals || {};
  const usageByKey = new Map((data?.byKey || []).map((k) => [k.key_hint, k]));
  const usageByModel = new Map((data?.byModel || []).map((m) => [m.model, m]));
  const series = dailySeries(range, data?.byDay, (r) => ({ input: num(r?.input_tokens), output: num(r?.output_tokens) }));
  const chartSeries = [
    { key: 'input', label: t('ai.input'), color: colors.series[0] },
    { key: 'output', label: t('ai.output'), color: colors.series[1] },
  ];

  return (
    <Box>
      <PageHeader title={t('ai.title')} subtitle={t('ai.subtitle')} right={<DateRangeBar value={range} onChange={setRange} />} />
      <ErrorBanner error={error} />

      <Box sx={{ display: 'grid', gap: 1.5, mb: 1.5, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' } }}>
        <StatTile loading={loading} label={t('ai.cost')} value={fmtUsd(totals.cost_usd)} hint={t('ai.costHint')} />
        <StatTile loading={loading} label={t('ai.tokens')} value={fmtTokens(totals.total_tokens)} hint={t('ai.requestsHint', { n: fmtNumber(totals.requests) })} />
        <StatTile loading={loading} label={t('ai.input')} value={fmtTokens(totals.input_tokens)} />
        <StatTile loading={loading} label={t('ai.output')} value={fmtTokens(totals.output_tokens)} hint={t('ai.outputHint')} />
      </Box>

      <Panel title={t('ai.perDay')} action={<Legend series={chartSeries} />} sx={{ mb: 1.5 }}>
        <StackedBarChart data={series} series={chartSeries} loading={loading} emptyText={t('ai.noUsage')} formatValue={fmtTokens} />
      </Panel>

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' } }}>
        <Card sx={{ overflowX: 'auto' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, px: 2.5, pt: 2.25 }}>{t('ai.keys')}</Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', px: 2.5, pb: 1 }}>{t('ai.keysHint')}</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('ai.key')}</TableCell>
                <TableCell>{t('ai.paid')}</TableCell>
                <TableCell align="right">{t('ai.tokens')}</TableCell>
                <TableCell align="right">{t('ai.cost')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data?.keys || []).map((k) => {
                const u = usageByKey.get(k.key_hint);
                return (
                  <TableRow key={k.key_hint}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: 13, minWidth: 52 }}>{k.key_hint}</Typography>
                        <InlineField
                          value={k.label}
                          placeholder={t('ai.label')}
                          onSave={(label) => save(() => http.put(`/admin/ai-usage/keys/${encodeURIComponent(k.key_hint)}`, { label, is_paid: k.is_paid }))}
                        />
                        {!k.configured ? <Chip size="small" variant="outlined" label={t('ai.removed')} /> : null}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Switch
                        size="small"
                        checked={Boolean(k.is_paid)}
                        onChange={(e) =>
                          save(() => http.put(`/admin/ai-usage/keys/${encodeURIComponent(k.key_hint)}`, { label: k.label, is_paid: e.target.checked }))
                        }
                      />
                      <Typography component="span" sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                        {k.is_paid ? t('ai.paid') : t('ai.free')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{fmtTokens(u?.total_tokens || 0)}</TableCell>
                    <TableCell align="right">{k.is_paid ? fmtUsd(u?.cost_usd || 0) : '—'}</TableCell>
                  </TableRow>
                );
              })}
              {!loading && !data?.keys?.length ? (
                <TableRow>
                  <TableCell colSpan={4} sx={{ color: 'text.disabled', textAlign: 'center', py: 3 }}>
                    {t('ai.noKeys')}
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </Card>

        <Card sx={{ overflowX: 'auto' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, px: 2.5, pt: 2.25 }}>{t('ai.models')}</Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', px: 2.5, pb: 1 }}>{t('ai.pricesHint')}</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('ai.model')}</TableCell>
                <TableCell>{t('ai.priceIn')}</TableCell>
                <TableCell>{t('ai.priceOut')}</TableCell>
                <TableCell align="right">{t('ai.tokens')}</TableCell>
                <TableCell align="right">{t('ai.cost')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data?.prices || []).map((p) => {
                const u = usageByModel.get(p.model);
                const savePrice = (field) => (v) =>
                  save(() =>
                    http.put('/admin/ai-usage/prices', {
                      model: p.model,
                      input_per_m: field === 'in' ? v : p.input_per_m ?? 0,
                      output_per_m: field === 'out' ? v : p.output_per_m ?? 0,
                    }),
                  );
                return (
                  <TableRow key={p.model}>
                    <TableCell sx={{ fontSize: 13 }}>{p.model}</TableCell>
                    <TableCell>
                      <InlineField type="number" width={96} adornment="$" value={p.input_per_m ?? ''} placeholder="0" onSave={savePrice('in')} />
                    </TableCell>
                    <TableCell>
                      <InlineField type="number" width={96} adornment="$" value={p.output_per_m ?? ''} placeholder="0" onSave={savePrice('out')} />
                    </TableCell>
                    <TableCell align="right">{fmtTokens(u?.total_tokens || 0)}</TableCell>
                    <TableCell align="right">{fmtUsd(u?.cost_usd || 0)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      </Box>
    </Box>
  );
}
